import { useState, useEffect } from 'react';
import { Mail, Send, Edit, X, User, Building, Briefcase, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import apiClient from '../utils/axios';
import DraftReviewCard from '../components/DraftReviewCard';

export default function AIDraftReview() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/ai-drafts?status=pending');
      setDrafts(response.data.drafts || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (draftId, editedDraft = null) => {
    try {
      await apiClient.post(`/ai-drafts/${draftId}/approve`, {
        edited_draft: editedDraft
      });
      alert('Draft approved and sent successfully!');
      fetchDrafts();
      setSelectedDraft(null);
    } catch (error) {
      console.error('Error approving draft:', error);
      alert(error.response?.data?.message || 'Error approving draft');
    }
  };

  const handleReject = async (draftId) => {
    if (!confirm('Reject this draft? You\'ll need to write your own response.')) {
      return;
    }

    try {
      await apiClient.post(`/ai-drafts/${draftId}/reject`);
      alert('Draft rejected');
      fetchDrafts();
      setSelectedDraft(null);
    } catch (error) {
      console.error('Error rejecting draft:', error);
      alert(error.response?.data?.message || 'Error rejecting draft');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!selectedDraft) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleApprove(selectedDraft.id);
      } else if (e.key === 'E' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        // Open editor (to be implemented)
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedDraft(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedDraft]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading drafts...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Mail size={32} className="text-purple-600" />
          AI Draft Review
        </h1>
        <p className="mt-2 text-gray-600">
          Review and approve AI-generated reply drafts ({drafts.length} pending)
        </p>
      </div>

      {drafts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <CheckCircle2 size={48} className="mx-auto mb-4 text-gray-400" />
          <p>No pending drafts. All caught up!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {drafts.map((draft) => (
            <DraftReviewCard
              key={draft.id}
              draft={draft}
              isSelected={selectedDraft?.id === draft.id}
              onSelect={() => setSelectedDraft(draft)}
              onApprove={() => handleApprove(draft.id)}
              onReject={() => handleReject(draft.id)}
              onEdit={(editedDraft) => handleApprove(draft.id, editedDraft)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


